import React from 'react';
import { Clock, MapPin } from 'lucide-react';

/**
 * Компонент отображения популярных часовых поясов России
 */
const PopularTimeZones = ({ russianCities, setFormData }) => {
  // Группируем города по часовым поясам
  const groupedTimeZones = {};
  
  russianCities.forEach(city => {
    const offset = city.utcOffset;
    if (!groupedTimeZones[offset]) {
      groupedTimeZones[offset] = [];
    }
    groupedTimeZones[offset].push(city);
  });
  
  // Сортируем часовые пояса по смещению
  const sortedOffsets = Object.keys(groupedTimeZones).sort((a, b) => {
    const aNum = parseInt(a.replace(/[^\d-]/g, ''), 10);
    const bNum = parseInt(b.replace(/[^\d-]/g, ''), 10);
    return aNum - bNum;
  });

  // Обработчик выбора города
  const handleCitySelect = (cityId) => {
    setFormData(prev => ({
      ...prev,
      targetCity: cityId,
      targetTimezone: russianCities.find(city => city.id === cityId)?.timezone || prev.targetTimezone
    }));
    
    // Автоматический скролл к форме конвертера
    const form = document.querySelector('form');
    form?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    
    // Автоматически нажимаем на кнопку расчета после выбора города
    setTimeout(() => {
      document.querySelector('button[type="submit"]')?.click();
    }, 300);
  };

  // Функция для получения текущего времени в указанном часовом поясе
  const getCurrentTimeInTimezone = (timezone) => {
    const now = new Date();
    
    const options = {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    };
    
    return new Intl.DateTimeFormat('ru-RU', options).format(now);
  };

  // Подготавливаем данные для карты часовых поясов России
  const timeZoneData = sortedOffsets.map(offset => {
    const cities = groupedTimeZones[offset];
    const mainCity = cities[0]; // Берем первый город как представителя часового пояса
    const currentTime = getCurrentTimeInTimezone(mainCity.timezone);
    
    return {
      offset,
      cities,
      mainCity,
      currentTime
    };
  });

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-6 mb-8">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
        <MapPin className="w-5 h-5 mr-2 text-red-500" />
        Часовые пояса России
      </h2>

      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {timeZoneData.map((zone) => (
              <div 
                key={zone.offset} 
                className="p-4 bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-600 rounded-lg"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">
                      UTC{zone.offset}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      МСК{zone.offset === '+3:00' 
                        ? '' 
                        : getOffsetFromMoscowTime(zone.offset)}
                    </p>
                  </div>
                  <div className="clock-display text-xl text-purple-700 dark:text-purple-300 font-mono">
                    {zone.currentTime}
                  </div>
                </div>
                
                <div className="space-y-1.5">
                  {zone.cities.map((city) => (
                    <button 
                      key={city.id}
                      onClick={() => handleCitySelect(city.id)}
                      className="block w-full text-left px-2 py-1 text-sm hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-md transition-colors"
                    >
                      <span className="font-medium text-gray-700 dark:text-gray-300">{city.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
        <p className="flex items-start">
          <Clock className="w-4 h-4 mr-2 text-blue-500 mt-0.5" />
          <span>
            Россия охватывает 11 часовых поясов от UTC+2 (Калининград) до UTC+12 (Камчатка). 
            Нажмите на город, чтобы быстро узнать текущее время в этом часовом поясе или сравнить с другим городом.
          </span>
        </p>
      </div>
    </div>
  );
};

// Вспомогательная функция для расчета разницы от московского времени
const getOffsetFromMoscowTime = (offset) => {
  const moscowOffset = 3; // UTC+3
  
  // Получаем числовое значение смещения
  const match = offset.match(/([+-])(\d+):(\d+)/);
  if (!match) return '';
  
  const sign = match[1];
  const hours = parseInt(match[2], 10);
  const minutes = parseInt(match[3], 10);
  
  const totalOffset = (sign === '+' ? 1 : -1) * (hours + minutes / 60);
  const diffFromMoscow = totalOffset - moscowOffset;
  
  if (diffFromMoscow === 0) return '';
  
  const diffSign = diffFromMoscow > 0 ? '+' : '';
  return `${diffSign}${diffFromMoscow}`;
};

export default PopularTimeZones;